import React, { useEffect } from "react";
import { useFormContext, useWatch } from "react-hook-form";
import InspectionTypeScheduleCalendar from "../../scheduler/components/InspectionTypeScheduleCalendar";
import useInspectionTypeScheduleCalendar from "../../scheduler/hooks/useInspectionTypeScheduleCalendar";
import { AM, PM } from "../../scheduler/utils/InspectionRequestPreferenceMeridiem";
import { IScheduleInspectionModalForm } from "../ScheduleInspectionModal";
import { Time, CalendarDate, parseDate } from "@internationalized/date";

interface IControlledInspectionTypeScheduleCalendar {
  inspectionId?: string;
  inspectionTypeId: string;
}
export default function ControlledInspectionTypeScheduleCalendar({
  inspectionTypeId,
  inspectionId = undefined,
}: IControlledInspectionTypeScheduleCalendar) {
  // When an inspector/day is selected in the calendar, update the existing form in the modal
  const { setValue } = useFormContext<IScheduleInspectionModalForm>();

  // Handles when the user selects a time/inspector on the scheduler component
  const onCalendarSelect = (inspectorId: string, day: string, meridiem: "AM" | "PM") => {
    setValue("assignee", Number(inspectorId));

    const inspectionDate = parseDate(day);
    setValue("inspectionDate", inspectionDate);

    if (meridiem === AM) {
      setValue("inspectionTime", "AM");
    } else if (meridiem === PM) {
      setValue("inspectionTime", "PM");
    } else {
      setValue("inspectionTime", null);
    }
  };

  const calendar = useInspectionTypeScheduleCalendar({
    inspectionId: inspectionId,
    inspectionTypeId: inspectionTypeId,
    onSelect: onCalendarSelect,
  });

  // Watch the selected date in the existing form and keep the calendars current week and selected date in sync
  const selectedDate = useWatch<CalendarDate | null>({ name: "inspectionDate" });
  useEffect(() => {
    if (selectedDate) {
      calendar.setCurrentWeek(selectedDate);
    }
    calendar.setSelectedDate(selectedDate ?? null);
  }, [selectedDate]);

  // Watch the assignee id in the existing form and update the calendar if it changes
  const selectedInspectorId = useWatch<string | null>({ name: "assignee" });
  useEffect(() => {
    calendar.setSelectedInspectorId(selectedInspectorId ?? null);
  }, [selectedInspectorId]);

  // Watch the selected time in the form and keep the selected meridiem in sync with the time
  const selectedInspectionTimeOrMeridiem = useWatch<Time | "AM" | "PM" | null>({ name: "inspectionTime" });
  useEffect(() => {
    const isAmSelected =
      !selectedInspectionTimeOrMeridiem ||
      selectedInspectionTimeOrMeridiem === "AM" ||
      (selectedInspectionTimeOrMeridiem !== "PM" && selectedInspectionTimeOrMeridiem.hour < 12);
    const isPmSelected =
      !selectedInspectionTimeOrMeridiem ||
      selectedInspectionTimeOrMeridiem === "PM" ||
      (selectedInspectionTimeOrMeridiem !== "AM" && selectedInspectionTimeOrMeridiem.hour >= 12);
    if (isAmSelected) {
      calendar.setSelectedMeridiem(AM);
    } else if (isPmSelected) {
      calendar.setSelectedMeridiem(PM);
    } else {
      calendar.setSelectedMeridiem(AM);
    }
  }, [selectedInspectionTimeOrMeridiem]);

  return <InspectionTypeScheduleCalendar {...calendar} />;
}
